import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Calendar } from "./ui/calendar";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectLabel,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { useToast } from "../hooks/use-toast";
import { CheckCircle2, Clock, CalendarDays, User, Phone, MapPin, Wrench, Loader2, ImagePlus, X, Camera } from "lucide-react";
import { SERVICES, TIME_SLOTS, PROPERTY_TYPES } from "../mock";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const createInitialForm = (serviceId = "") => ({
  service: serviceId,
  customService: "",
  date: undefined,
  time: "",
  name: "",
  phone: "",
  email: "",
  address: "",
  suburb: "",
  notes: "",
  message: "",
});

export default function BookingModal({ open, onOpenChange, defaultService, defaultQuoteId }) {
  const { toast } = useToast();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);

  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(() => createInitialForm());
  const [submitted, setSubmitted] = useState(false);
  const [smsStatus, setSmsStatus] = useState({ sent: false, error: null });
  const [reference, setReference] = useState("");
  const [images, setImages] = useState([]); // { filename, mime_type, data_base64, preview }

  // Build combined options: regular services + quote options for property types
  const QUOTE_OPTIONS = PROPERTY_TYPES.map((p) => ({
    id: `quote-${p.id}`,
    title: `Book a Quote — ${p.title}`,
    isQuote: true,
  }));
  const ALL_OPTIONS = [
    ...SERVICES.map((s) => ({ id: s.id, title: s.title, isQuote: false })),
    ...QUOTE_OPTIONS,
  ];

  useEffect(() => {
    if (open) {
      setStep(1);
      setSubmitted(false);
      setSubmitting(false);
      setSmsStatus({ sent: false, error: null });
      setReference("");
      setImages([]);
      setForm(createInitialForm(defaultQuoteId || defaultService?.id || ""));
    }
  }, [open, defaultService, defaultQuoteId]);

  const resetAndClose = () => {
    setStep(1);
    setSubmitted(false);
    setSubmitting(false);
    setSmsStatus({ sent: false, error: null });
    setReference("");
    setImages([]);
    setForm(createInitialForm(defaultQuoteId || defaultService?.id || ""));
    onOpenChange(false);
  };

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const isCustomService = form.service === "custom";
  const canNext1 =
    form.service && form.date && form.time && (!isCustomService || form.customService.trim().length >= 3);
  const canSubmit =
    form.name.trim() && form.phone.trim().length >= 8 && form.address.trim() && form.suburb.trim();

  const selectedOption = ALL_OPTIONS.find((o) => o.id === form.service);
  const selectedServiceTitle = isCustomService
    ? form.customService.trim() || "Other / Custom request"
    : selectedOption?.title || "";
  const isQuoteBooking = !!selectedOption?.isQuote;

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const remaining = 5 - images.length;
    if (remaining <= 0) {
      toast({
        title: "Maximum 5 images",
        description: "Please remove an image before adding another.",
        variant: "destructive",
      });
      return;
    }
    const toAdd = files.slice(0, remaining);
    const next = [];
    for (const file of toAdd) {
      if (!file.type.startsWith("image/")) {
        toast({
          title: "Only images allowed",
          description: `${file.name} is not an image.`,
          variant: "destructive",
        });
        continue;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "Image too large",
          description: `${file.name} is over 5 MB.`,
          variant: "destructive",
        });
        continue;
      }
      // read as base64
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const base64 = String(dataUrl).split(",")[1] || "";
      next.push({
        filename: file.name,
        mime_type: file.type || "image/jpeg",
        data_base64: base64,
        preview: dataUrl,
      });
    }
    if (next.length) setImages((arr) => [...arr, ...next]);
  };

  const removeImage = (idx) => {
    setImages((arr) => arr.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      const combinedNotes = [
        (form.notes || "").trim(),
        (form.message || "").trim() ? `Message: ${(form.message || "").trim()}` : "",
      ].filter(Boolean).join("\n\n");

      const payload = {
        service: form.service,
        service_title: selectedServiceTitle,
        date: form.date.toISOString().slice(0, 10),
        time: form.time,
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: (form.email || "").trim(),
        address: form.address.trim(),
        suburb: form.suburb.trim(),
        notes: combinedNotes,
        booking_type: selectedOption?.isQuote ? "quote" : "service",
        images: images.map(({ filename, mime_type, data_base64 }) => ({
          filename,
          mime_type,
          data_base64,
        })),
      };
      const res = await axios.post(`${API}/bookings`, payload);
      setReference(res.data.reference || "");
      setSmsStatus({ sent: !!res.data.sms_sent, error: res.data.sms_error || null });
      setSubmitted(true);
      toast({
        title: res.data.sms_sent ? "Booking confirmed — SMS sent!" : "Booking confirmed",
        description: res.data.sms_sent
          ? `We've texted a confirmation to ${form.phone}.`
          : `We'll call ${form.phone} to confirm your appointment.`,
      });
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || "Unknown error";
      toast({
        title: "Couldn't submit booking",
        description: typeof detail === "string" ? detail : "Please try again or call us.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const formattedDate = form.date
    ? form.date.toLocaleDateString("en-AU", {
        weekday: "long",
        day: "numeric",
        month: "long",
      })
    : "";

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => (nextOpen ? onOpenChange(true) : resetAndClose())}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto p-0 gap-0">
        {/* header */}
        <div className="hero-gradient text-white p-6 rounded-t-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl font-extrabold flex items-center gap-2">
              <Wrench size={22} className="text-sky-300" />
              Book Your Plumber
            </DialogTitle>
            <DialogDescription className="text-white/85">
              Same-day appointments available across Sydney. We&apos;ll confirm by phone within 30 minutes.
            </DialogDescription>
          </DialogHeader>

          {/* steps */}
          {!submitted && (
            <div className="flex items-center gap-2 mt-5">
              {[1, 2].map((n) => (
                <div key={n} className="flex items-center gap-2 flex-1">
                  <div
                    className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                      step >= n ? "bg-sky-400 text-[#07304f]" : "bg-white/20 text-white/70"
                    }`}
                  >
                    {n}
                  </div>
                  <div className="text-xs font-semibold">
                    {n === 1 ? "Service & Time" : "Your Details"}
                  </div>
                  {n === 1 && <div className="flex-1 h-px bg-white/20" />}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-6">
          {submitted ? (
            <div className="text-center py-6">
              <div className="w-16 h-16 rounded-full bg-sky-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 size={36} className="text-sky-600" />
              </div>
              <h3 className="text-2xl font-extrabold text-[#0a4f8a] mb-2">Booking Request Received</h3>
              <p className="text-slate-600 mb-3">
                Thanks {form.name.split(" ")[0]}! We&apos;ve received your request for{" "}
                <strong>{selectedServiceTitle}</strong> on{" "}
                <strong>{formattedDate}</strong> at <strong>{form.time}</strong>.
                <br />
                We&apos;ll confirm your date & time shortly.
              </p>
              {smsStatus.sent ? (
                <p className="text-sky-700 text-sm font-semibold mb-5 flex items-center justify-center gap-1.5">
                  <CheckCircle2 size={16} /> SMS receipt sent to {form.phone}
                </p>
              ) : (
                <p className="text-slate-500 text-sm mb-5">
                  We&apos;ll call you on <strong>{form.phone}</strong> shortly to confirm.
                </p>
              )}
              <div className="bg-sky-50 border border-sky-100 rounded-lg p-4 text-left text-sm space-y-2 mb-6">
                {reference && (
                  <div className="font-bold text-[#0a4f8a]">Reference: {reference}</div>
                )}
                <div className="flex items-center gap-2"><Wrench size={14} className="text-sky-600" /> <span>{selectedServiceTitle}</span></div>
                <div className="flex items-center gap-2"><CalendarDays size={14} className="text-sky-600" /> <span>{formattedDate}</span></div>
                <div className="flex items-center gap-2"><Clock size={14} className="text-sky-600" /> <span>{form.time}</span></div>
                <div className="flex items-center gap-2"><MapPin size={14} className="text-sky-600" /> <span>{form.address}, {form.suburb}</span></div>
              </div>
              <Button
                onClick={resetAndClose}
                className="bg-[#0a4f8a] hover:bg-[#083d6e] text-white font-semibold px-6"
                data-testid="booking-success-close-button"
              >
                Close
              </Button>
            </div>
          ) : step === 1 ? (
            <div className="space-y-5">
              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-2 block">Select a service or request a quote</Label>
                <Select value={form.service} onValueChange={(v) => update("service", v)}>
                  <SelectTrigger className="h-11" data-testid="booking-service-select">
                    <SelectValue placeholder="Choose a service or quote option" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel>Plumbing Services</SelectLabel>
                      {SERVICES.map((s) => (
                        <SelectItem key={s.id} value={s.id} data-testid={`booking-service-option-${s.id}`}>
                          {s.title}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                    <SelectGroup>
                      <SelectLabel>Book a Quote</SelectLabel>
                      {PROPERTY_TYPES.map((p) => (
                        <SelectItem
                          key={`quote-${p.id}`}
                          value={`quote-${p.id}`}
                          data-testid={`booking-service-option-quote-${p.id}`}
                        >
                          Book a Quote — {p.title}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                    <SelectGroup>
                      <SelectLabel>Other</SelectLabel>
                      <SelectItem value="custom" data-testid="booking-service-option-custom">
                        Other / Custom request…
                      </SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>

                {isCustomService && (
                  <div className="mt-3">
                    <Label htmlFor="customService" className="text-xs font-semibold text-slate-600 mb-1.5 block">
                      Tell us what you need *
                    </Label>
                    <Input
                      id="customService"
                      value={form.customService}
                      onChange={(e) => update("customService", e.target.value)}
                      placeholder="e.g. Outdoor tap replacement, dishwasher install, rainwater tank…"
                      className="h-11"
                      data-testid="booking-custom-service-input"
                      maxLength={120}
                    />
                  </div>
                )}
              </div>

              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <Label className="text-sm font-semibold text-slate-700 mb-2 block">
                    Choose a date
                  </Label>
                  <button
                    type="button"
                    onClick={() => update("date", tomorrow)}
                    className="mb-2 text-xs font-semibold text-sky-700 hover:text-sky-900 underline underline-offset-2"
                    data-testid="booking-select-next-available-date-button"
                  >
                    Select next available date
                  </button>
                  <div className="border rounded-lg p-2 bg-white">
                    <Calendar
                      mode="single"
                      selected={form.date}
                      onSelect={(d) => update("date", d)}
                      disabled={(d) => d < today}
                      className="mx-auto"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-semibold text-slate-700 mb-2 block">
                    Choose a time (hourly)
                  </Label>
                  <div className="grid grid-cols-3 gap-2 max-h-[320px] overflow-y-auto p-1">
                    {TIME_SLOTS.map((t) => (
                      <button
                        key={t}
                        onClick={() => update("time", t)}
                        data-testid={`booking-time-slot-${t.replace(/[:\s]/g, "-").toLowerCase()}`}
                        className={`px-2 py-2 rounded-md text-sm font-semibold border transition-all ${
                          form.time === t
                            ? "bg-sky-600 text-white border-sky-600 shadow"
                            : "bg-white text-slate-700 border-slate-200 hover:border-sky-400"
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                  <div className="text-xs text-slate-500 mt-2 flex items-center gap-1">
                    <Clock size={12} /> All times AEST (Sydney). Hourly slots.
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-2">
                <Button
                  onClick={() => setStep(2)}
                  disabled={!canNext1}
                  className="bg-[#0a4f8a] hover:bg-[#083d6e] text-white font-semibold px-6 disabled:opacity-50"
                  data-testid="booking-continue-button"
                >
                  Continue
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* summary chip */}
              {selectedOption && (
                <div className="flex items-center gap-3 bg-sky-50 border border-sky-100 rounded-lg p-3 text-sm">
                  <CheckCircle2 size={18} className="text-sky-600 shrink-0" />
                  <div className="text-slate-700">
                    <strong>{selectedServiceTitle}</strong> • {formattedDate} • {form.time}
                  </div>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name" className="text-sm font-semibold text-slate-700 mb-1.5 block">
                    Full name *
                  </Label>
                  <div className="relative">
                    <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      id="name"
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      placeholder="John Smith"
                      className="pl-9 h-11"
                      data-testid="booking-name-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="phone" className="text-sm font-semibold text-slate-700 mb-1.5 block">
                    Phone number *
                  </Label>
                  <div className="relative">
                    <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      id="phone"
                      type="tel"
                      value={form.phone}
                      onChange={(e) => update("phone", e.target.value)}
                      placeholder="0412 345 678"
                      className="pl-9 h-11"
                      data-testid="booking-phone-input"
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="email" className="text-sm font-semibold text-slate-700 mb-1.5 block">
                  Email (optional)
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  placeholder="you@email.com"
                  className="h-11"
                  data-testid="booking-email-input"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="address" className="text-sm font-semibold text-slate-700 mb-1.5 block">
                    Street address *
                  </Label>
                  <Input
                    id="address"
                    value={form.address}
                    onChange={(e) => update("address", e.target.value)}
                    placeholder="12 George St"
                    className="h-11"
                    data-testid="booking-address-input"
                  />
                </div>
                <div>
                  <Label htmlFor="suburb" className="text-sm font-semibold text-slate-700 mb-1.5 block">
                    Suburb *
                  </Label>
                  <Input
                    id="suburb"
                    value={form.suburb}
                    onChange={(e) => update("suburb", e.target.value)}
                    placeholder="Parramatta"
                    className="h-11"
                    data-testid="booking-suburb-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="notes-issue" className="text-sm font-semibold text-slate-700 mb-1.5 block">
                  Describe the issue (optional)
                </Label>
                <Textarea
                  id="notes-issue"
                  value={form.notes}
                  onChange={(e) => update("notes", e.target.value)}
                  placeholder="e.g. Leaking kitchen tap and slow drain in bathroom sink..."
                  rows={2}
                  data-testid="booking-issue-textarea"
                />
              </div>

              {/* Image attachments */}
              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">
                  {isQuoteBooking ? "Attach photos for your quote (recommended)" : "Attach photos (optional)"}
                </Label>
                <p className="text-xs text-slate-500 mb-2">
                  {isQuoteBooking
                    ? "Adding photos helps us prepare an accurate quote before we contact you. Up to 5 images, 5 MB each."
                    : "Photos help our plumber prepare for the job. Up to 5 images, 5 MB each."}
                </p>

                {images.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-3">
                    {images.map((img, idx) => (
                      <div key={idx} className="relative group rounded-lg overflow-hidden border border-slate-200 aspect-square bg-slate-50">
                        <img src={img.preview} alt={img.filename} className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => removeImage(idx)}
                          className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 hover:bg-red-600 text-white flex items-center justify-center transition-colors"
                          aria-label="Remove image"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {images.length < 5 && (
                  <label
                    htmlFor="booking-images"
                    className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-300 hover:border-sky-400 hover:bg-sky-50/50 rounded-lg p-5 cursor-pointer transition-colors text-center"
                  >
                    <div className="flex items-center gap-2 text-[#0a4f8a]">
                      <ImagePlus size={20} />
                      <span className="font-semibold text-sm">
                        {images.length === 0 ? "Add photos" : "Add more photos"}
                      </span>
                    </div>
                    <span className="text-xs text-slate-500 flex items-center gap-1">
                      <Camera size={12} /> Drag &amp; drop or click to upload ({5 - images.length} remaining)
                    </span>
                    <input
                      id="booking-images"
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={(e) => {
                        handleFiles(e.target.files);
                        e.target.value = "";
                      }}
                    />
                  </label>
                )}
              </div>

              {/* Notes / Message to us */}
              <div>
                <Label htmlFor="message" className="text-sm font-semibold text-slate-700 mb-1.5 block">
                  Notes / Message to us (optional)
                </Label>
                <Textarea
                  id="message"
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  placeholder="Anything else we should know? Access instructions, preferred contact time, parking, pets on-site, special requests…"
                  rows={3}
                  data-testid="booking-message-textarea"
                />
              </div>

              <div className="flex justify-between pt-3 gap-3">
                <Button
                  variant="outline"
                  onClick={() => setStep(1)}
                  className="border-slate-300"
                  data-testid="booking-back-button"
                >
                  Back
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={!canSubmit || submitting}
                  className="bg-sky-600 hover:bg-sky-700 text-white font-bold px-7 disabled:opacity-50"
                  data-testid="booking-submit-button"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting...
                    </>
                  ) : (
                    "Confirm Booking"
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
